import { ReactNode, useRef, useEffect, useState, createContext, useContext } from 'react';
import { motion, useScroll, useTransform, MotionValue, useSpring } from 'motion/react';
import { useReducedMotion } from './useScrollProgress';

// Context for sharing scroll progress with child components
interface ScrollLockContextValue {
  progress: MotionValue<number>;
  isLocked: boolean;
}

const ScrollLockContext = createContext<ScrollLockContextValue | null>(null);

export function useScrollLockProgress() {
  const context = useContext(ScrollLockContext);
  if (!context) {
    throw new Error('useScrollLockProgress must be used within a ScrollLockSection');
  }
  return context;
}

interface ScrollytellingContainerProps {
  children: ReactNode;
  className?: string;
  id?: string;
  /** Height multiplier for scroll distance (e.g., 2 = 200vh of scroll) */
  scrollHeight?: number;
}

/**
 * Container for scrollytelling sections with sticky left / visual right layout
 * Uses CSS sticky positioning for the left column while the right scrolls
 */
export function ScrollytellingContainer({
  children,
  className = '',
  id,
  scrollHeight = 1.5,
}: ScrollytellingContainerProps) {
  return (
    <section
      id={id}
      className={`relative ${className}`}
      style={{ minHeight: `${scrollHeight * 100}vh` }}
    >
      {children}
    </section>
  );
}

interface StickyColumnProps {
  children: ReactNode;
  className?: string;
  /** Top offset when sticky (default: 50% to center) */
  stickyTop?: string;
}

/**
 * Sticky column for narrative text (left side on desktop)
 */
export function StickyColumn({
  children,
  className = '',
  stickyTop = '50%',
}: StickyColumnProps) {
  return (
    <div
      className={`lg:sticky lg:top-0 lg:h-screen lg:flex lg:items-center ${className}`}
      style={{ top: stickyTop }}
    >
      <div className="lg:w-[40%] lg:pr-12 px-6 py-12 lg:py-0">
        {children}
      </div>
    </div>
  );
}

interface VisualColumnProps {
  children: ReactNode;
  className?: string;
}

/**
 * Visual column (right side on desktop) - contains scroll-animated content
 */
export function VisualColumn({ children, className = '' }: VisualColumnProps) {
  return (
    <div className={`lg:ml-[40%] lg:w-[60%] px-6 py-12 ${className}`}>
      {children}
    </div>
  );
}

interface ScrollytellingGridProps {
  /** Content for the sticky left column */
  stickyContent: ReactNode;
  /** Content for the scrolling right column */
  visualContent: ReactNode;
  className?: string;
  id?: string;
  /**
   * Enable scroll-lock behavior where section freezes while content animates.
   * When true, the section locks in place and scroll progress drives animations.
   * @default true
   */
  scrollLock?: boolean;
  /**
   * Height multiplier for scroll distance when scroll-lock is enabled.
   * Higher values = more scroll distance before section unlocks.
   * @default 2 (200vh of scroll distance)
   */
  scrollHeight?: number;
}

/**
 * Complete scrollytelling grid with sticky left and scrolling right
 *
 * When scrollLock is enabled (default), the section "freezes" in the viewport
 * while the user continues scrolling. The scroll progress drives animations
 * on the right-side content. Only after the content is fully revealed does
 * the section "unlock" and normal scrolling resumes.
 *
 * This creates an immersive scroll-jacking effect for key storytelling sections.
 */
export function ScrollytellingGrid({
  stickyContent,
  visualContent,
  className = '',
  id,
  scrollLock = true,
  scrollHeight = 2,
}: ScrollytellingGridProps) {
  const reducedMotion = useReducedMotion();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLocked, setIsLocked] = useState(false);

  // Track scroll progress within this section
  const { scrollYProgress } = useScroll({
    target: containerRef,
    // Start when top of container reaches top of viewport
    // End when bottom of container reaches top of viewport
    offset: ['start start', 'end start'],
  });

  // Smooth out the scroll progress
  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  });

  // Track when we're in the locked section
  useEffect(() => {
    if (!scrollLock) return;

    const unsubscribe = scrollYProgress.on('change', (value) => {
      // We're "locked" when progress is between 0 and 1
      setIsLocked(value > 0 && value < 1);
    });

    return () => unsubscribe();
  }, [scrollYProgress, scrollLock]);

  // If scroll-lock is disabled or reduced motion is preferred, use simple layout
  if (!scrollLock || reducedMotion) {
    return (
      <section
        ref={containerRef}
        id={id}
        className={`relative py-8 lg:py-12 ${className}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-[35%_65%] lg:gap-12 lg:items-start">
            {/* Sticky Left Column */}
            <div className="lg:sticky lg:top-20 self-start pb-8 lg:pb-0">
              <motion.div
                initial={reducedMotion ? {} : { opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: '-100px' }}
                transition={{ duration: 0.6 }}
              >
                {stickyContent}
              </motion.div>
            </div>

            {/* Scrolling Right Column */}
            <div className="space-y-8">
              <div className="max-w-4xl">
                {visualContent}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Scroll-lock enabled layout
  return (
    <ScrollLockContext.Provider value={{ progress: smoothProgress, isLocked }}>
      <section
        ref={containerRef}
        id={id}
        className={`relative ${className}`}
        style={{
          // Create scroll distance for the animation
          height: `${scrollHeight * 100}vh`,
        }}
      >
        {/* Sticky container that stays fixed during scroll */}
        <div
          className="sticky top-0 h-screen flex items-center overflow-hidden"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className="lg:grid lg:grid-cols-[35%_65%] lg:gap-12 lg:items-center">
              {/* Left Column - Narrative Content */}
              <div className="pb-8 lg:pb-0">
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: '-100px' }}
                  transition={{ duration: 0.6 }}
                >
                  {stickyContent}
                </motion.div>
              </div>

              {/* Right Column - Visual Content (animated by scroll) */}
              <div className="space-y-8 max-h-[80vh] overflow-y-auto scrollbar-hide">
                <div className="max-w-4xl">
                  {visualContent}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Progress indicator (optional, shows scroll progress within section) */}
        <ScrollLockProgressIndicator progress={smoothProgress} />
      </section>
    </ScrollLockContext.Provider>
  );
}

/**
 * Visual progress indicator for scroll-lock sections
 */
function ScrollLockProgressIndicator({ progress }: { progress: MotionValue<number> }) {
  const scaleX = useTransform(progress, [0, 1], [0, 1]);

  return (
    <motion.div
      className="fixed bottom-0 left-0 right-0 h-1 bg-eventus-purple/30 z-40 pointer-events-none lg:block hidden"
      style={{ opacity: useTransform(progress, [0, 0.02, 0.98, 1], [0, 1, 1, 0]) }}
    >
      <motion.div
        className="h-full bg-eventus-purple origin-left"
        style={{ scaleX }}
      />
    </motion.div>
  );
}

interface ScrollProgressBarProps {
  className?: string;
}

/**
 * Visual progress bar showing scroll position within the page
 */
export function ScrollProgressBar({ className = '' }: ScrollProgressBarProps) {
  const { scrollYProgress } = useScroll();
  const reducedMotion = useReducedMotion();

  const scaleX = useTransform(scrollYProgress, [0, 1], [0, 1]);

  if (reducedMotion) return null;

  return (
    <motion.div
      className={`fixed top-0 left-0 right-0 h-1 bg-eventus-purple origin-left z-50 ${className}`}
      style={{ scaleX }}
    />
  );
}

interface StepIndicatorProps {
  steps: number;
  activeStep: number;
  className?: string;
}

/**
 * Dot indicators showing current position in a multi-step section
 */
export function StepIndicator({
  steps,
  activeStep,
  className = '',
}: StepIndicatorProps) {
  return (
    <div className={`flex gap-2 ${className}`}>
      {Array.from({ length: steps }).map((_, i) => (
        <motion.div
          key={i}
          className={`w-2 h-2 rounded-full transition-colors ${
            i === activeStep ? 'bg-eventus-purple' : 'bg-gray-300'
          }`}
          animate={{ scale: i === activeStep ? 1.2 : 1 }}
          transition={{ type: 'spring', stiffness: 300, damping: 20 }}
        />
      ))}
    </div>
  );
}

interface TextStepProps {
  children: ReactNode;
  isActive: boolean;
  className?: string;
}

/**
 * Individual text step within a sticky column
 * Fades in/out based on active state
 */
export function TextStep({ children, isActive, className = '' }: TextStepProps) {
  const reducedMotion = useReducedMotion();

  return (
    <motion.div
      className={`${className}`}
      initial={reducedMotion ? { opacity: 1 } : { opacity: 0, y: 20 }}
      animate={
        reducedMotion
          ? { opacity: isActive ? 1 : 0.3 }
          : { opacity: isActive ? 1 : 0.3, y: isActive ? 0 : 10 }
      }
      transition={{ duration: 0.4 }}
    >
      {children}
    </motion.div>
  );
}

interface AnimatedVisualProps {
  children: ReactNode;
  progress: number;
  className?: string;
}

/**
 * Wrapper for scroll-animated visual content
 */
export function AnimatedVisual({
  children,
  progress,
  className = '',
}: AnimatedVisualProps) {
  const reducedMotion = useReducedMotion();
  const opacity = Math.min(1, progress * 2);

  return (
    <motion.div
      className={className}
      style={reducedMotion ? {} : { opacity }}
    >
      {children}
    </motion.div>
  );
}

// ============================================================================
// Scroll-Lock Animated Components
// ============================================================================

interface ScrollRevealItemProps {
  children: ReactNode;
  /** Index of this item in the sequence (0-based) */
  index: number;
  /** Total number of items in the sequence */
  total: number;
  className?: string;
  /** Animation variant */
  variant?: 'fade' | 'slide-up' | 'slide-right' | 'scale';
}

/**
 * Content that reveals based on scroll progress within a ScrollytellingGrid.
 * Use inside the visualContent prop of ScrollytellingGrid.
 *
 * Items are revealed sequentially - earlier items appear first as user scrolls.
 */
export function ScrollRevealItem({
  children,
  index,
  total,
  className = '',
  variant = 'fade',
}: ScrollRevealItemProps) {
  const context = useContext(ScrollLockContext);
  const reducedMotion = useReducedMotion();
  const [isVisible, setIsVisible] = useState(false);

  // Calculate when this item should be visible based on progress
  // Divide the scroll into segments, one per item
  const startThreshold = index / total;

  useEffect(() => {
    if (!context) {
      // If not in a scroll-lock context, just show immediately
      setIsVisible(true);
      return;
    }

    const unsubscribe = context.progress.on('change', (value) => {
      // Item becomes visible when we've scrolled past its start threshold
      // and stays visible
      setIsVisible(value >= startThreshold);
    });

    return () => unsubscribe();
  }, [context, startThreshold]);

  // Animation variants
  const variants = {
    fade: {
      hidden: { opacity: 0 },
      visible: { opacity: 1 },
    },
    'slide-up': {
      hidden: { opacity: 0, y: 30 },
      visible: { opacity: 1, y: 0 },
    },
    'slide-right': {
      hidden: { opacity: 0, x: -30 },
      visible: { opacity: 1, x: 0 },
    },
    scale: {
      hidden: { opacity: 0, scale: 0.9 },
      visible: { opacity: 1, scale: 1 },
    },
  };

  if (reducedMotion) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      className={className}
      initial="hidden"
      animate={isVisible ? 'visible' : 'hidden'}
      variants={variants[variant]}
      transition={{ duration: 0.5, ease: 'easeOut' }}
    >
      {children}
    </motion.div>
  );
}

interface ScrollProgressValueProps {
  /** Starting value */
  from: number;
  /** Ending value */
  to: number;
  /** When in the scroll progress this animation should start (0-1) */
  startAt?: number;
  /** When in the scroll progress this animation should end (0-1) */
  endAt?: number;
  /** Number of decimal places */
  decimals?: number;
  /** Prefix (e.g., "$") */
  prefix?: string;
  /** Suffix (e.g., "%") */
  suffix?: string;
  className?: string;
}

/**
 * Animated number that changes based on scroll progress.
 * Must be used within a ScrollytellingGrid with scrollLock enabled.
 */
export function ScrollProgressValue({
  from,
  to,
  startAt = 0,
  endAt = 1,
  decimals = 0,
  prefix = '',
  suffix = '',
  className = '',
}: ScrollProgressValueProps) {
  const context = useContext(ScrollLockContext);
  const [value, setValue] = useState(from);

  useEffect(() => {
    if (!context) {
      setValue(to);
      return;
    }

    const unsubscribe = context.progress.on('change', (progress) => {
      // Normalize progress to the startAt/endAt range
      const normalizedProgress = Math.max(0, Math.min(1, (progress - startAt) / (endAt - startAt)));
      const newValue = from + (to - from) * normalizedProgress;
      setValue(newValue);
    });

    return () => unsubscribe();
  }, [context, from, to, startAt, endAt]);

  return (
    <span className={className}>
      {prefix}
      {value.toFixed(decimals)}
      {suffix}
    </span>
  );
}

interface ScrollProgressBarFillProps {
  /** Maximum width as percentage (0-100) */
  maxWidth: number;
  /** When in the scroll progress this animation should start (0-1) */
  startAt?: number;
  /** When in the scroll progress this animation should end (0-1) */
  endAt?: number;
  /** Bar color */
  color?: string;
  /** Background color */
  bgColor?: string;
  /** Height in pixels */
  height?: number;
  className?: string;
  /** Label to show on the bar */
  label?: string;
}

/**
 * Progress bar that fills based on scroll progress.
 * Must be used within a ScrollytellingGrid with scrollLock enabled.
 */
export function ScrollProgressBarFill({
  maxWidth,
  startAt = 0,
  endAt = 1,
  color = 'bg-eventus-purple',
  bgColor = 'bg-gray-200',
  height = 32,
  className = '',
  label,
}: ScrollProgressBarFillProps) {
  const context = useContext(ScrollLockContext);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!context) {
      setWidth(maxWidth);
      return;
    }

    const unsubscribe = context.progress.on('change', (progress) => {
      const normalizedProgress = Math.max(0, Math.min(1, (progress - startAt) / (endAt - startAt)));
      setWidth(maxWidth * normalizedProgress);
    });

    return () => unsubscribe();
  }, [context, maxWidth, startAt, endAt]);

  return (
    <div className={`relative ${bgColor} rounded-lg overflow-hidden ${className}`} style={{ height }}>
      <motion.div
        className={`absolute inset-y-0 left-0 ${color} rounded-lg flex items-center justify-end px-3`}
        style={{ width: `${width}%` }}
        initial={{ width: 0 }}
        animate={{ width: `${width}%` }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
      >
        {label && width > 10 && (
          <span className="text-white text-sm font-medium truncate">{label}</span>
        )}
      </motion.div>
    </div>
  );
}

interface ScrollStepperProps {
  /** Array of step content */
  steps: ReactNode[];
  className?: string;
  /** Gap between steps */
  gap?: number;
}

/**
 * Container that reveals steps sequentially based on scroll progress.
 * Must be used within a ScrollytellingGrid with scrollLock enabled.
 */
export function ScrollStepper({
  steps,
  className = '',
  gap = 6,
}: ScrollStepperProps) {
  return (
    <div className={`space-y-${gap} ${className}`}>
      {steps.map((step, index) => (
        <ScrollRevealItem
          key={index}
          index={index}
          total={steps.length}
          variant="slide-up"
        >
          {step}
        </ScrollRevealItem>
      ))}
    </div>
  );
}
